import { z } from "zod";
import { DbBool, IsoDateTime, SpotifyIdSchema, jsonColumn } from "./common.ts";

export const PlaylistSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  is_public: DbBool.nullable(),
  collaborative: DbBool.nullable(),
  owner_id: z.string().nullable(),
  owner_name: z.string().nullable(),
  owned_by_user: DbBool,
  /** Lets sync skip a playlist whose contents have not changed. */
  snapshot_id: z.string().nullable(),
  track_count: z.number().int().nullable(),
  /** 0 when Spotify refused the item listing, so the UI can say why it is empty. */
  items_accessible: DbBool,
  external_url: z.string().nullable(),
  /** Spotify CDN image when the API sent one. */
  image_url: z.string().nullable(),
  /**
   * List / sidebar thumb: Spotify image, else first track's cover.
   * Later we can fill `cover_urls` (up to 4) for a mosaic without changing callers
   * that only read `cover_url`.
   */
  cover_url: z.string().nullable(),
  updated_at: IsoDateTime,
  /** Index in Spotify’s /me/playlists library order (null until next playlist sync). */
  library_position: z.number().nullable().optional(),
});

export type Playlist = z.infer<typeof PlaylistSchema>;

export const PlaylistTrackSchema = z.object({
  playlist_id: SpotifyIdSchema,
  track_id: SpotifyIdSchema,
  /** Integer, and part of the primary key — Spotify allows a track twice. */
  position: z.number().int().nonnegative(),
  added_at: IsoDateTime.nullable(),
});

export type PlaylistTrack = z.infer<typeof PlaylistTrackSchema>;

export const HiddenPlaylistSchema = z.object({
  playlist_id: SpotifyIdSchema,
  hidden_at: IsoDateTime,
});

export type HiddenPlaylist = z.infer<typeof HiddenPlaylistSchema>;

/**
 * Spotify playlist row for Browse → Playlists. Counts are derived in SQL from
 * the local mirror (no Spotify API).
 */
export const PlaylistBrowseMatchViaSchema = z.enum(["name", "artist", "album", "track"]);

export const PlaylistBrowseItemSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string(),
  cover_url: z.string().nullable(),
  owned_by_user: DbBool,
  owner_name: z.string().nullable(),
  /** Tracks in `playlist_tracks` that still exist in `tracks`. */
  tracks_in_library: z.number().int().nonnegative(),
  artist_count: z.number().int().nonnegative(),
  album_count: z.number().int().nonnegative(),
  /** Tracks with `entity_status.status` set and not `none`. */
  statused_count: z.number().int().nonnegative(),
  /**
   * Earliest `playlist_tracks.added_at` in this playlist (Spotify does not give
   * a follow date on /me/playlists). Null when items were never synced.
   */
  added_at: z.preprocess((v) => {
    if (v == null || v === "") return null;
    if (typeof v !== "string") return null;
    const ms = Date.parse(v);
    if (Number.isNaN(ms)) return null;
    return new Date(ms).toISOString();
  }, IsoDateTime.nullable()),
  /**
   * Top user-taxonomy genre ids among playlist tracks (≤5) — same idea as
   * sheet “computed” chips. Used by Browse → Playlists genre filter.
   */
  genre_ids: jsonColumn(z.array(z.string())).default([]),
  /**
   * Why this row matched a search. Null when the browse list is unfiltered
   * (no `q`) or the match is the playlist name alone.
   */
  match_via: PlaylistBrowseMatchViaSchema.nullable().optional().default(null),
  /** Sample matching artist / album / track name when `match_via` is content-based. */
  match_label: z.string().nullable().optional().default(null),
});

export type PlaylistBrowseItem = z.infer<typeof PlaylistBrowseItemSchema>;
export type PlaylistBrowseMatchVia = z.infer<typeof PlaylistBrowseMatchViaSchema>;
